import React, { useEffect, useState } from "react";
import {
  Eye,
  EyeOff,
  Mail,
  Lock,
  ArrowRight,
  AlertCircle,
  ShieldCheck,
} from "lucide-react";
import { useAuth } from "../store/hooks/useRedux";
import { useNavigate } from "react-router-dom";
import useSEO from "../hooks/useSEO";
import AuthLayout, { fieldClass, labelClass, fieldIconClass, AuthEyebrow } from "../components/Layout/AuthLayout";

const VendorSignIn = () => {
  useSEO({
    title: "Vendor Sign In",
    description:
      "Sign in to your MandapAI vendor dashboard to manage listings, bookings, leads and AI growth tools.",
    path: "/vendor-signin",
  });
  const navigate = useNavigate();
  const { login, getUserInfo, isAuthenticated } = useAuth();
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/dashboard", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }
    if (!formData.password) {
      newErrors.password = "Password is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSignIn = async () => {
    if (!validateForm()) return;
    setLoading(true);
    setErrors({});
    try {
      await login({ email: formData.email, password: formData.password });
      await getUserInfo().catch(() => {});
      navigate("/dashboard", { replace: true });
    } catch (err) {
      const message = err?.message || "Invalid credentials. Please try again.";
      setErrors({ submit: message });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") handleSignIn();
  };

  return (
    <AuthLayout>
      <div className="mb-7">
        <AuthEyebrow icon={Lock}>Vendor sign in</AuthEyebrow>
        <h1 className="font-['Manrope'] text-3xl font-extrabold tracking-tight text-gray-900">
          Welcome back
        </h1>
        <p className="mt-2 text-[15px] text-gray-500">
          Sign in to your vendor dashboard.
        </p>
      </div>

      {errors.submit && (
        <div className="mb-4 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-3.5">
          <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-500" />
          <p className="text-sm text-red-600">{errors.submit}</p>
        </div>
      )}

      <div className="space-y-5">
        {/* Email */}
        <div>
          <label className={labelClass}>
            Email address
          </label>
          <div className="group/field relative">
            <Mail className={fieldIconClass} />
            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              onKeyPress={handleKeyPress}
              className={fieldClass(errors.email)}
              placeholder="you@business.com"
              autoComplete="email"
            />
          </div>
          {errors.email && (
            <p className="mt-1.5 text-xs font-medium text-red-500">{errors.email}</p>
          )}
        </div>

        {/* Password */}
        <div>
          <div className="mb-1.5 flex items-center justify-between">
            <label className="text-[13px] font-semibold text-gray-700">Password</label>
            <button
              type="button"
              onClick={() => navigate("/vendor-forgot-password")}
              className="text-[13px] font-semibold text-gray-900 underline decoration-purple-300 decoration-2 underline-offset-4 transition-colors hover:decoration-purple-600"
            >
              Forgot password?
            </button>
          </div>
          <div className="group/field relative">
            <Lock className={fieldIconClass} />
            <input
              type={showPassword ? "text" : "password"}
              value={formData.password}
              onChange={(e) => handleInputChange("password", e.target.value)}
              onKeyPress={handleKeyPress}
              className={`${fieldClass(errors.password)} !pr-12`}
              placeholder="Enter your password"
              autoComplete="current-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 transition-colors hover:text-gray-600"
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <EyeOff className="h-[18px] w-[18px]" /> : <Eye className="h-[18px] w-[18px]" />}
            </button>
          </div>
          {errors.password && (
            <p className="mt-1.5 text-xs font-medium text-red-500">{errors.password}</p>
          )}
        </div>

        {/* Sign In */}
        <button
          onClick={handleSignIn}
          disabled={loading}
          className="group flex w-full items-center justify-center gap-2 h-12 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 px-5 text-[15px] font-['Manrope'] font-semibold text-white shadow-lg shadow-purple-600/20 transition-all duration-200 hover:shadow-xl hover:shadow-purple-600/30 disabled:cursor-not-allowed disabled:opacity-70 focus:outline-none focus:ring-2 focus:ring-purple-500/40"
        >
          {loading ? (
            <>
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
              <span>Signing in…</span>
            </>
          ) : (
            <>
              <span>Sign in</span>
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </>
          )}
        </button>
      </div>

      <div className="my-6 flex items-center gap-3">
        <span className="h-px flex-1 bg-gray-200" />
        <span className="text-xs font-medium text-gray-400">New to MandapAI?</span>
        <span className="h-px flex-1 bg-gray-200" />
      </div>

      <button
        type="button"
        onClick={() => navigate("/vendor-registration")}
        className="flex h-12 w-full items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white text-[15px] font-['Manrope'] font-semibold text-gray-900 transition-colors hover:border-purple-200 hover:bg-purple-50"
      >
        Become a vendor
      </button>

      <div className="mt-6 flex items-center justify-center gap-2 rounded-xl border border-gray-200/70 bg-gray-50/70 px-3 py-2.5 text-xs font-medium text-gray-500">
        <ShieldCheck className="h-4 w-4 flex-shrink-0 text-green-500" />
        Bank-grade encryption · Trusted by 25,000+ vendors
      </div>
    </AuthLayout>
  );
};

export default VendorSignIn;
